'use client';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import CreateAccountCard from './createAccountCard';
import useHash from '@/customHooks/useHash';
import { type SignUpDetails, signupSchema } from '@/schemas/signupSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Grid,
  Stack,
  Box,
  Typography,
  Card,
  Divider,
  CardHeader,
  CardContent,
  Skeleton,
  Alert
} from '@mui/material';
import { PAGE_PATHS } from '@/constants/constants';
import SelectedPlanCard from '@/components/Products/SelectPlan/SelectedPlanCard';
import { useCheckout } from '@stripe/react-stripe-js/checkout';
import { useUser } from '@/utils/supabase/useUser';
import PaymentCard from './PaymentCard';

const PaymentSummary = () => {
  const checkoutState = useCheckout();

  if (checkoutState.type === 'loading') {
    return (
      <Stack spacing={1}>
        <Skeleton variant='text' />
        <Skeleton variant='text' />
        <Divider />
        <Skeleton variant='text' />
      </Stack>
    );
  }

  if (checkoutState.type === 'error') {
    return <Alert severity='error'>Error: {checkoutState.error.message}</Alert>;
  }

  const checkout = checkoutState.checkout;
  const { total, taxAmounts } = checkout;
  console.log('Checkout State in PaymentSummary:', checkout);
  console.log('Total:', total);
  console.log('Tax Amounts:', taxAmounts);
  const { amount } = total.total;

  const paymentLine = (
    leftSide: string,
    rightSide: string,
    typographyProps?: React.ComponentProps<typeof Typography>
  ) => (
    <Box
      fontWeight='bold'
      key={leftSide}
      display='flex'
      justifyContent='space-between'
    >
      <Typography {...typographyProps}>{leftSide}</Typography>
      <Typography {...typographyProps}>{rightSide}</Typography>
    </Box>
  );

  return (
    <Stack spacing={1}>
      {paymentLine('Subtotal', total.subtotal.amount)}
      {taxAmounts?.map((tax) =>
        paymentLine(
          `${tax.displayName} (${tax.inclusive ? 'Inclusive' : 'Exclusive'})`,
          tax.amount
        )
      )}
      <Divider />
      {paymentLine('Total Today', amount, { fontWeight: 'bold' })}
    </Stack>
  );
};

const PaymentSummaryCard = () => {
  return (
    <Card>
      <CardHeader
        title='Payment Summary'
        titleTypographyProps={{ variant: 'h2' }}
      />
      <CardContent>
        <PaymentSummary />
      </CardContent>
    </Card>
  );
};

const SignupForm = ({
  lineItems
}: {
  lineItems?: StripeLineItemWithPriceProduct[] | null;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const hash = useHash();
  const { user, supabase } = useUser();

  const redirectParam = searchParams.get('redirect');

  const redirectURL = redirectParam
    ? `${redirectParam}${hash ? `#${hash}` : ''}`
    : null;

  const params = new URLSearchParams(searchParams.toString());
  const nextPath = `${pathname}?${params.toString()}${hash ? `#${hash}` : ''}`;

  const methods = useForm<SignUpDetails>({
    defaultValues: { firstName: '', lastName: '', email: '', mobile: '' },
    mode: 'onTouched',
    resolver: async (data, context, options) => {
      const result = await zodResolver(signupSchema)(data, context, options);
      console.log('Validation Result:', result);
      return result;
    }
  });

  const { handleSubmit } = methods;

  const submitHandler = handleSubmit(async (data) => {
    const redirectTo = new URL(PAGE_PATHS.authVerify, window.location.origin);
    redirectTo.searchParams.set('next', nextPath);
    if (redirectURL) redirectTo.searchParams.set('redirect', redirectURL);

    const { error } = await supabase.auth.signInWithOtp({
      email: data.email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectTo.toString(),
        data: {
          full_name: `${data.firstName} ${data.lastName}`,
          first_name: data.firstName,
          last_name: data.lastName,
          mobile: data.mobile
        }
      }
    });

    if (error) {
      console.error('Error sending OTP:', error);
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('email', data.email);
    router.replace('/signup/confirm?' + params.toString());
  });

  const onChangePlanButtonClick = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('product');
    return `${PAGE_PATHS.membershipPlans}?${params.toString()}`;
  };

  return (
    <Grid container direction={{ xs: 'column-reverse', sm: 'row' }}>
      <Grid item xs={12} md>
        <Stack>
          <FormProvider {...methods}>
            <form onSubmit={submitHandler}>
              <CreateAccountCard
                nextPath={nextPath || undefined}
                redirectURL={redirectURL || undefined}
              />
            </form>
          </FormProvider>
          {lineItems && <PaymentCard />}
        </Stack>
      </Grid>
      {lineItems && (
        <Grid item xs={12} md={4}>
          <Stack>
            <SelectedPlanCard
              lineItems={lineItems}
              // product={product}
              onChangeLink={onChangePlanButtonClick}
            />
            {lineItems && user && <PaymentSummaryCard />}
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

export default SignupForm;
